import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import {
  MIN_RECORD_SECONDS,
  RECORD_SECONDS,
  assertClipSize,
  buildFaceCapture,
  pickRecorderMime,
  type FaceCapture,
} from "../lib/face-capture";

type Phase = "booting" | "ready" | "recording" | "processing" | "preview";

export function cameraErrorMessage(err: unknown): string {
  const name =
    err && typeof err === "object" && "name" in err
      ? String((err as { name: string }).name)
      : "";
  if (name === "NotAllowedError" || name === "PermissionDeniedError") {
    return "دسترسی به دوربین یا میکروفون رد شد. لطفاً از تنظیمات مرورگر اجازهٔ هر دو را بدهید و دوباره تلاش کنید.";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return "دوربین یا میکروفونی روی این دستگاه پیدا نشد.";
  }
  if (name === "NotReadableError" || name === "TrackStartError") {
    return "دوربین در حال استفاده توسط برنامه دیگری است. آن را ببندید و دوباره تلاش کنید.";
  }
  if (name === "SecurityError" || name === "NotSupportedError") {
    return "برای دسترسی به دوربین باید صفحه از طریق HTTPS یا localhost باز شود.";
  }
  if (name === "OverconstrainedError") {
    return "تنظیمات دوربین با این دستگاه سازگار نیست. دوباره تلاش کنید.";
  }
  if (err instanceof Error && err.message) return err.message;
  return "امکان باز کردن دوربین وجود ندارد.";
}

/**
 * Start getUserMedia in the same user-gesture that opens the modal
 * (required on many mobile Safari / WebView builds). Camera + microphone.
 */
export function requestLiveCameraStream(): Promise<MediaStream> {
  if (typeof window !== "undefined" && window.isSecureContext === false) {
    return Promise.reject(
      Object.assign(new Error(cameraErrorMessage({ name: "SecurityError" })), {
        name: "SecurityError",
      }),
    );
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return Promise.reject(
      Object.assign(
        new Error(
          "مرورگر شما از ضبط زنده پشتیبانی نمی‌کند. از «انتخاب فایل» استفاده کنید یا مرورگر را به‌روز کنید.",
        ),
        { name: "NotSupportedError" },
      ),
    );
  }

  // Sound is required: the identity service checks the spoken sentence.
  const audio: MediaTrackConstraints = { echoCancellation: true, noiseSuppression: true };
  const attempts: MediaStreamConstraints[] = [
    {
      audio,
      video: { facingMode: { ideal: "user" }, width: { ideal: 640 }, height: { ideal: 720 } },
    },
    { audio, video: { facingMode: "user" } },
    { audio: true, video: true },
  ];

  let lastErr: unknown;
  const run = async (): Promise<MediaStream> => {
    for (const constraints of attempts) {
      try {
        return await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        lastErr = err;
      }
    }
    throw lastErr instanceof Error ? lastErr : new Error(cameraErrorMessage(lastErr));
  };
  return run();
}

type Props = {
  open: boolean;
  busy?: boolean;
  confirmLabel?: string;
  busyLabel?: string;
  /** Sentence to read aloud (from the API — speech recognition compares against it). */
  line: string;
  /** Promise started in the open-button click handler (gesture-safe). */
  bootstrapStream?: Promise<MediaStream> | null;
  onClose: () => void;
  onConfirm: (capture: FaceCapture) => void | Promise<void>;
};

/**
 * Live camera + MediaRecorder modal for video identity: records a short video
 * with sound in which the person reads `line` aloud. Mirrors sign-gateway
 * GatewayVideoCaptureModal with reader CSS.
 */
export function LiveVideoCaptureModal({
  open,
  busy,
  confirmLabel = "تأیید و ارسال",
  busyLabel = "در حال احراز هویت…",
  line,
  bootstrapStream = null,
  onClose,
  onConfirm,
}: Props) {
  const liveVideoRef = useRef<HTMLVideoElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const autoStopRef = useRef<number | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const sessionRef = useRef(0);

  const [phase, setPhase] = useState<Phase>("booting");
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [capture, setCapture] = useState<FaceCapture | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [portalReady, setPortalReady] = useState(false);

  useEffect(() => {
    setPortalReady(true);
  }, []);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const clearTimers = () => {
    for (const ref of [timerRef, autoStopRef]) {
      if (ref.current != null) {
        window.clearInterval(ref.current);
        window.clearTimeout(ref.current);
        ref.current = null;
      }
    }
  };

  const revokePreview = () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = null;
    setPreviewUrl(null);
    setCapture(null);
  };

  const resetRecordingState = () => {
    clearTimers();
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.onstop = null;
      try {
        recorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    recorderRef.current = null;
    chunksRef.current = [];
    setElapsed(0);
    revokePreview();
  };

  const bindLiveElement = async (stream: MediaStream) => {
    const el = liveVideoRef.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream;
    el.muted = true;
    el.playsInline = true;
    el.setAttribute("playsinline", "true");
    el.setAttribute("webkit-playsinline", "true");
    await el.play().catch(() => undefined);
  };

  const adoptStream = async (stream: MediaStream, session: number) => {
    if (session !== sessionRef.current) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    stopStream();
    streamRef.current = stream;
    await bindLiveElement(stream);
    if (session !== sessionRef.current) {
      stopStream();
      return;
    }
    setPhase("ready");
    setError(null);
  };

  const attachStream = async (prefer?: Promise<MediaStream> | null) => {
    const session = sessionRef.current;
    setError(null);
    setPhase("booting");
    resetRecordingState();
    stopStream();

    if (typeof window !== "undefined" && window.isSecureContext === false) {
      setError(cameraErrorMessage({ name: "SecurityError" }));
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setError(
        "مرورگر شما از ضبط زنده پشتیبانی نمی‌کند. از «انتخاب فایل» استفاده کنید یا مرورگر را به‌روز کنید.",
      );
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      setError(
        "ضبط ویدیو در این مرورگر پشتیبانی نمی‌شود. لطفاً با دوربین گوشی ضبط کنید و فایل را انتخاب کنید.",
      );
      return;
    }

    try {
      let stream: MediaStream | null = null;
      if (prefer) {
        try {
          const warmed = await prefer;
          const alive = warmed.getTracks().some((t) => t.readyState === "live");
          if (alive) stream = warmed;
          else warmed.getTracks().forEach((t) => t.stop());
        } catch {
          stream = null;
        }
      }
      if (!stream) stream = await requestLiveCameraStream();
      await adoptStream(stream, session);
    } catch (err) {
      if (session !== sessionRef.current) return;
      stopStream();
      setError(cameraErrorMessage(err));
    }
  };

  useEffect(() => {
    if (!open) {
      sessionRef.current += 1;
      clearTimers();
      resetRecordingState();
      stopStream();
      setPhase("booting");
      setError(null);
      setSubmitting(false);
      return;
    }
    const session = ++sessionRef.current;
    void attachStream(bootstrapStream);
    return () => {
      if (sessionRef.current === session) sessionRef.current += 1;
      clearTimers();
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.onstop = null;
        try {
          recorderRef.current.stop();
        } catch {
          /* ignore */
        }
      }
      recorderRef.current = null;
      stopStream();
      revokePreview();
    };
  }, [open, bootstrapStream]);

  useEffect(() => {
    if (!open || phase === "preview" || !streamRef.current) return;
    void bindLiveElement(streamRef.current);
  }, [open, phase, portalReady]);

  useEffect(() => {
    if (phase === "preview" && previewUrl && previewVideoRef.current) {
      const el = previewVideoRef.current;
      el.src = previewUrl;
      el.muted = true;
      void el.play().catch(() => undefined);
    }
  }, [phase, previewUrl]);

  const stopRecording = () => {
    clearTimers();
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") return;
    try {
      recorder.stop();
    } catch {
      setError("توقف ضبط با خطا مواجه شد.");
    }
  };

  const finishRecording = async (blob: Blob, mime: string, session: number) => {
    try {
      assertClipSize(blob);
      if (session !== sessionRef.current) return;
      const next = buildFaceCapture(blob, mime);
      previewUrlRef.current = URL.createObjectURL(next.clip);
      setCapture(next);
      setPreviewUrl(previewUrlRef.current);
      setPhase("preview");
      liveVideoRef.current?.pause();
    } catch (err) {
      if (session !== sessionRef.current) return;
      setError(err instanceof Error ? err.message : "پردازش ویدیو ناموفق بود. دوباره ضبط کنید.");
      setPhase("ready");
    }
  };

  const startRecording = () => {
    const stream = streamRef.current;
    if (!stream || busy || submitting) return;
    setError(null);
    revokePreview();
    chunksRef.current = [];
    const session = sessionRef.current;

    if (!stream.getAudioTracks().some((t) => t.readyState === "live")) {
      setError("میکروفون فعال نیست؛ برای احراز هویت باید صدای شما هم ضبط شود.");
      return;
    }
    const mime = pickRecorderMime();
    let recorder: MediaRecorder;
    try {
      // ~10s stays well under the 10MB upload limit
      const options: MediaRecorderOptions = { videoBitsPerSecond: 700_000, audioBitsPerSecond: 48_000 };
      if (mime) options.mimeType = mime;
      recorder = new MediaRecorder(stream, options);
    } catch {
      setError(
        "شروع ضبط ممکن نشد. لطفاً از «انتخاب فایل» استفاده کنید یا مرورگر دیگری را امتحان کنید.",
      );
      return;
    }

    const usedMime = recorder.mimeType || mime || "video/webm";
    recorder.ondataavailable = (ev) => {
      if (ev.data.size > 0) chunksRef.current.push(ev.data);
    };
    recorder.onerror = () => {
      setError("خطا در حین ضبط ویدیو رخ داد.");
      clearTimers();
      setPhase("ready");
    };
    recorder.onstop = () => {
      clearTimers();
      const blob = new Blob(chunksRef.current, { type: usedMime });
      chunksRef.current = [];
      setPhase("processing");
      void finishRecording(blob, usedMime, session);
    };

    recorderRef.current = recorder;
    try {
      recorder.start(250);
    } catch {
      try {
        recorder.start();
      } catch {
        setError(
          "شروع ضبط ممکن نشد. لطفاً از «انتخاب فایل» استفاده کنید یا مرورگر دیگری را امتحان کنید.",
        );
        recorderRef.current = null;
        return;
      }
    }
    setElapsed(0);
    setPhase("recording");

    timerRef.current = window.setInterval(() => {
      setElapsed((s) => Math.min(s + 1, RECORD_SECONDS));
    }, 1000);
    autoStopRef.current = window.setTimeout(stopRecording, RECORD_SECONDS * 1000);
  };

  const reRecord = () => {
    if (busy || submitting) return;
    revokePreview();
    setElapsed(0);
    setError(null);
    setPhase("ready");
    const el = liveVideoRef.current;
    if (el && streamRef.current) {
      el.srcObject = streamRef.current;
      void el.play().catch(() => undefined);
    } else {
      void attachStream();
    }
  };

  const confirm = async () => {
    if (!capture || busy || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(capture);
    } catch (err) {
      setError(err instanceof Error ? err.message : "ارسال ویدیو ناموفق بود");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (busy || submitting) return;
    sessionRef.current += 1;
    clearTimers();
    resetRecordingState();
    stopStream();
    onClose();
  };

  if (!open || !portalReady) return null;

  const locked = Boolean(busy || submitting);
  const remaining = Math.max(0, RECORD_SECONDS - elapsed);

  return createPortal(
    <div className="live-capture-backdrop" role="presentation">
      <div
        className="live-capture-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="ضبط ویدیو احراز هویت"
        dir="rtl"
      >
        <div className="live-capture-head">
          <p className="live-capture-title">احراز هویت ویدیویی</p>
          <button
            type="button"
            className="icon-btn"
            disabled={locked}
            onClick={handleClose}
            aria-label="بستن"
            title="بستن"
          >
            <X size={16} />
          </button>
        </div>

        <div className="live-capture-body">
          {phase === "preview" ? (
            <p className="signing-muted">
              ویدیو را (با صدا) بررسی کنید. اگر چهره واضح است و جمله کامل و شنیدنی خوانده شده،
              تأیید کنید؛ در غیر این صورت دوباره ضبط کنید.
            </p>
          ) : (
            <ul className="live-capture-tips">
              <li>در نور کافی و جای کم‌صدا، رو به دوربین و بدون عینک آفتابی یا ماسک باشید.</li>
              <li>صورت را کامل داخل کادر بیضی قرار دهید.</li>
              <li>
                پس از «شروع ضبط»، جملهٔ زیر را با صدای بلند و واضح بخوانید (حداکثر{" "}
                {RECORD_SECONDS} ثانیه).
              </li>
            </ul>
          )}
          {phase !== "preview" ? <p className="live-capture-line">«{line}»</p> : null}

          <div className="live-capture-stage">
            <video
              ref={liveVideoRef}
              className={
                phase === "preview"
                  ? "live-capture-video is-mirrored is-hidden"
                  : "live-capture-video is-mirrored"
              }
              playsInline
              muted
              autoPlay
            />
            <video
              ref={previewVideoRef}
              className={
                phase !== "preview" ? "live-capture-video is-hidden" : "live-capture-video"
              }
              playsInline
              muted
              controls
              loop
            />
            {phase === "ready" || phase === "recording" ? (
              <div
                aria-hidden
                className={
                  phase === "recording"
                    ? "live-capture-oval is-recording"
                    : "live-capture-oval"
                }
              />
            ) : null}
            {phase === "recording" ? (
              <div className="live-capture-rec-badge">
                <span className="live-capture-rec-dot" />
                در حال ضبط · {remaining}ث
              </div>
            ) : null}
            {(phase === "booting" && !error) || phase === "processing" ? (
              <div className="live-capture-booting">
                {phase === "processing" ? "در حال آماده‌سازی ویدیو…" : "در حال باز کردن دوربین…"}
              </div>
            ) : null}
          </div>

          {error ? <p className="live-capture-error">{error}</p> : null}

          <div className="live-capture-actions">
            {phase === "ready" || phase === "booting" ? (
              <button
                type="button"
                className="primary-btn"
                disabled={locked || phase !== "ready"}
                onClick={startRecording}
              >
                شروع ضبط
              </button>
            ) : null}

            {phase === "recording" ? (
              <button
                type="button"
                className="danger-btn live-capture-full"
                disabled={locked || elapsed < MIN_RECORD_SECONDS}
                onClick={stopRecording}
              >
                {elapsed < MIN_RECORD_SECONDS ? "در حال ضبط…" : "پایان ضبط"}
              </button>
            ) : null}

            {phase === "preview" ? (
              <>
                <button
                  type="button"
                  className="primary-btn"
                  disabled={locked || !capture}
                  onClick={() => void confirm()}
                >
                  {locked ? busyLabel : confirmLabel}
                </button>
                <button
                  type="button"
                  className="ghost-btn live-capture-full"
                  disabled={locked}
                  onClick={reRecord}
                >
                  ضبط مجدد
                </button>
              </>
            ) : null}

            {error && phase === "booting" ? (
              <button
                type="button"
                className="ghost-btn live-capture-full"
                disabled={locked}
                onClick={() => void attachStream()}
              >
                تلاش دوباره برای دوربین
              </button>
            ) : null}

            <button
              type="button"
              className="ghost-btn live-capture-full"
              disabled={locked || phase === "recording" || phase === "processing"}
              onClick={handleClose}
            >
              انصراف
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
